import React, { useState, useEffect, useRef } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { Visibility, ArrowBack, MarkEmailRead } from "@mui/icons-material";
import style from "../Styles/loginPage.module.css";
import {
  Button,
  Paper,
  IconButton,
  InputAdornment,
  Typography,
  CircularProgress,
} from "@mui/material";
import { postData } from "../Services/postData";
import { urlEnum } from "../Utils/urlEnum";
import { useNavigate } from "react-router-dom";
import axios from "axios";

type AuthStep = "credentials" | "2fa";

const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Stores authentication data in localStorage and fetches cache data
 */
async function storeAuthData(token: string, user: { id: string; name: string; email: string }, permissions: { name: string }[], roles: { name: string }[]): Promise<void> {
  const request = axios.create({
    headers: { Authorization: `Bearer ${token}` },
  });

  // Enable loading
  const loadingElement = document.getElementById("loading");
  loadingElement?.classList.remove("loadingDisabled");
  loadingElement?.classList.add("loading");

  try {
    const cacheDataResponse = await request.get(`${urlEnum.cacheData}/null`);

    if (cacheDataResponse?.data) {
      localStorage.setItem("cacheData", JSON.stringify(cacheDataResponse.data));
      localStorage.setItem("useId", user.id);
      localStorage.setItem("userName", user.name);
      localStorage.setItem("userEmail", user.email);
      localStorage.setItem("access_token", token);

      // Save permissions
      const permissionNames = permissions.map((p) => p.name);
      localStorage.setItem("permissions", permissionNames.join(","));

      // Save roles
      const roleNames = roles.map((r) => r.name);
      localStorage.setItem("roles", roleNames.join(","));
    }
  } finally {
    // Disable loading
    loadingElement?.classList.remove("loading");
    loadingElement?.classList.add("loadingDisabled");
  }
}

/**
 * Navigates to the appropriate dashboard based on user permissions
 */
function navigateToDashboard(navigate: ReturnType<typeof useNavigate>): void {
  const data = window.localStorage.getItem("permissions");
  if (data) {
    const formattedData = data.split(",");
    document.cookie = "accept_notification = false;";
    navigate(getPermissions(takeEachRoute(formattedData)));
    window.location.reload();
  }
}

export default function Login() {
  const navigate = useNavigate();
  const [step, setStep] = useState<AuthStep>("credentials");
  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // 2FA state
  const [tempToken, setTempToken] = useState("");
  const [tempEmail, setTempEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [isResending, setIsResending] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const isSubmitting = useRef(false);

  // Auto-focus code input when entering 2FA step
  useEffect(() => {
    if (step === "2fa" && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [step]);

  // Countdown timer for resend button
  useEffect(() => {
    if (step !== "2fa" || resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [step, resendCooldown]);

  async function autoSubmitCode(code: string) {
    if (isSubmitting.current || isLoading || code.length !== 6 || !tempToken) return;
    isSubmitting.current = true;
    setIsLoading(true);
    setErrorText("");

    try {
      const response = (await postData(urlEnum.verify2FA, {
        code,
        two_factor_token: tempToken,
      })) as {
        data?: {
          token: string;
          user: { id: string; name: string; email: string };
          permissions: { name: string }[];
          roles: { name: string }[];
        };
      };

      if (response?.data) {
        await storeAuthData(response.data.token, response.data.user, response.data.permissions, response.data.roles);
        navigateToDashboard(navigate);
      }
    } catch (err: unknown) {
      const error = err as { response?: { status: number; data?: { error?: string } }; message?: string };
      if (error.response) {
        const msg = error.response.data?.error?.toLowerCase() || "";
        if (error.response.status === 401 && msg.includes("expired")) {
          setErrorText("Codul a expirat. Vă rugăm să solicitați un cod nou.");
        } else {
          setErrorText("Cod invalid. Vă rugăm să încercați din nou.");
        }
      } else {
        setErrorText("A apărut o eroare. Vă rugăm să încercați din nou.");
      }
      setVerificationCode("");
      codeInputRef.current?.focus();
    } finally {
      setIsLoading(false);
      isSubmitting.current = false;
    }
  }

  function handleCodeChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value.replace(/\D/g, "").slice(0, 6);
    setVerificationCode(value);
    setErrorText("");
    if (value.length === 6) autoSubmitCode(value);
  }

  function handleCodePaste(event: React.ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();
    const value = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    setVerificationCode(value);
    setErrorText("");
    if (value.length === 6) autoSubmitCode(value);
  }

  function onChangeTextField(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setUser((prev) => ({ ...prev, [name]: value }));
  }

  async function handleCredentialsSubmit() {
    setIsLoading(true);
    setErrorText("");

    try {
      const response = (await postData(urlEnum.login, user)) as {
        data?: {
          requires_2fa?: boolean;
          two_factor_token?: string;
          token?: string;
          user?: { id: string; name: string; email: string };
          permissions?: { name: string }[];
          roles?: { name: string }[];
        };
      };

      if (response?.data) {
        // Check if 2FA is required
        if (response.data.requires_2fa && response.data.two_factor_token) {
          setTempToken(response.data.two_factor_token);
          setTempEmail(user.email);
          setStep("2fa");
          setResendCooldown(RESEND_COOLDOWN_SECONDS);
          return;
        }

        // Regular login (no 2FA)
        if (response.data.token && response.data.user && response.data.permissions && response.data.roles) {
          await storeAuthData(
            response.data.token,
            response.data.user,
            response.data.permissions,
            response.data.roles
          );
          navigateToDashboard(navigate);
        }
      }
    } catch (err: unknown) {
      const error = err as { response?: { status: number }; message?: string };
      if (error.response?.status === 401) {
        setErrorText("Parola sau nume utilizator greșite!");
      } else if (error.message) {
        setErrorText(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerifyCode() {
    if (verificationCode.length !== 6) return;
    autoSubmitCode(verificationCode);
  }

  async function handleResendCode() {
    if (resendCooldown > 0 || isResending || !tempToken) return;

    setIsResending(true);
    setErrorText("");

    try {
      await postData(urlEnum.resend2FA, {
        two_factor_token: tempToken,
      });

      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setVerificationCode("");
      codeInputRef.current?.focus();
    } catch (err: unknown) {
      const error = err as { response?: { status: number } };
      if (error.response?.status === 401) {
        setErrorText("Sesiunea a expirat. Vă rugăm să vă autentificați din nou.");
        handleBackToCredentials();
      } else {
        setErrorText("Nu s-a putut retrimite codul. Încercați din nou.");
      }
    } finally {
      setIsResending(false);
    }
  }

  function handleBackToCredentials() {
    setStep("credentials");
    setTempToken("");
    setTempEmail("");
    setVerificationCode("");
    setErrorText("");
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
  }

  return (
    <div className={style.App}>
      <div className={style.loginContainer} style={{ height: step === "2fa" ? "auto" : undefined }}>
        <div style={{ margin: 30 }}>
          <img src="/logoAutodel.png" alt="No file" width="300" />
        </div>
        <Paper className={style.loginFields}>
          {step === "credentials" ? (
            <ValidatorForm className={style.form} onSubmit={handleCredentialsSubmit}>
              <TextValidator
                variant="filled"
                required
                id="email"
                name="email"
                label="Email"
                validators={["required"]}
                errorMessages={["Câmpul este obligatoriu"]}
                value={user.email}
                onChange={onChangeTextField}
                className={style.textfield}
                disabled={isLoading}
              />
              <TextValidator
                variant="filled"
                required
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                label="Parola"
                validators={["required"]}
                errorMessages={["Câmpul este obligatoriu"]}
                value={user.password}
                onChange={onChangeTextField}
                className={style.textfield}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        style={{ color: "white" }}
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isLoading}
                      >
                        <Visibility />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                type="submit"
                className={style.submit}
                disabled={isLoading}
              >
                Logare
              </Button>
              {errorText && <Typography className={style.errorText}>{errorText}</Typography>}
            </ValidatorForm>
          ) : (
            <ValidatorForm className={style.form} onSubmit={handleVerifyCode}>
              <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginBottom: 24
              }}>
                <div style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: "#FF663320",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 16
                }}>
                  <MarkEmailRead style={{ fontSize: 32, color: "#FF6633" }} />
                </div>
                <Typography variant="h6" style={{ color: "#4D5E80", fontWeight: 600 }}>
                  Verificare în doi pași
                </Typography>
              </div>

              <div style={{
                backgroundColor: "#f5f5f5",
                borderRadius: 8,
                padding: "12px 16px",
                marginBottom: 24,
                textAlign: "center"
              }}>
                <Typography variant="body2" style={{ color: "#666" }}>
                  Am trimis un cod de verificare la:
                </Typography>
                <Typography variant="body1" style={{ color: "#4D5E80", fontWeight: 600, marginTop: 4 }}>
                  {tempEmail}
                </Typography>
              </div>

              <TextValidator
                inputRef={codeInputRef}
                variant="outlined"
                required
                id="code"
                name="code"
                placeholder="------"
                validators={["required", "matchRegexp:^[0-9]{6}$"]}
                errorMessages={["Câmpul este obligatoriu", "Codul trebuie să aibă 6 cifre"]}
                value={verificationCode}
                onChange={handleCodeChange}
                onPaste={handleCodePaste}
                disabled={isLoading}
                fullWidth
                inputProps={{
                  maxLength: 6,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  autoComplete: "one-time-code",
                  style: {
                    letterSpacing: "0.8em",
                    textAlign: "center",
                    fontSize: "1.8rem",
                    fontWeight: 600,
                    padding: "16px"
                  },
                }}
                style={{ marginBottom: 8 }}
              />

              <Typography variant="caption" style={{ color: "#999", marginBottom: 16, display: "block", textAlign: "center" }}>
                Introdu codul din 6 cifre
              </Typography>

              {errorText && (
                <div style={{
                  backgroundColor: "#ffebee",
                  borderRadius: 8,
                  padding: "12px 16px",
                  marginBottom: 16,
                  textAlign: "center"
                }}>
                  <Typography style={{ color: "#c62828", fontSize: 14 }}>
                    {errorText}
                  </Typography>
                </div>
              )}

              <div style={{
                display: "flex",
                justifyContent: "center",
                gap: 8,
                marginTop: 8
              }}>
                <Button
                  variant="text"
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0 || isResending}
                  size="small"
                  style={{
                    color: resendCooldown > 0 ? "#999" : "#FF6633",
                    textTransform: "none"
                  }}
                >
                  {isResending ? (
                    <CircularProgress size={16} style={{ marginRight: 8 }} />
                  ) : null}
                  {resendCooldown > 0 ? `Retrimite (${resendCooldown}s)` : "Retrimite codul"}
                </Button>
                <span style={{ color: "#ddd" }}>|</span>
                <Button
                  variant="text"
                  onClick={handleBackToCredentials}
                  size="small"
                  startIcon={<ArrowBack style={{ fontSize: 16 }} />}
                  style={{ color: "#4D5E80", textTransform: "none" }}
                >
                  Înapoi
                </Button>
              </div>
            </ValidatorForm>
          )}
        </Paper>
      </div>
    </div>
  );
}

export const getPermissions = (id: any) => {
  switch (id) {
    case "homepage":
      return "/home";
      case "vehiclesDashboard":
        return "/vehiclesDashboard";
    case "financialDepartment":
      return "/financialDepartment";
    case "logistics":
      return "/logistics";
    case "sales":
      return "/sales";
    case "acquisitions":
      return "/acquisitions";
    default:
      return "/";
  }
};

export const takeEachRoute = (data: any) => {
  if (data.includes("homepage")) {
    return "homepage";
  }else 
  if (data.includes("vehiclesDashboard")) {
    return "vehiclesDashboard";
  } else if (data.includes("acquisitions")) {
    return "acquisitions";
  } else if (data.includes("sales")) {
    return "sales";
  } else if (data.includes("financialDepartment")) {
    return "financialDepartment";
  } else if (data.includes("logistics")) {
    return "logistics";
  }
};
